import * as XLSX from 'xlsx';

export interface ICustomColumn {
    /** The name of the column to be used in the exported data header. */
    name: string;
    /** The key to identify the metric in the data source. */
    key: string;
    /** Indicates whether this column should be used as the row key in the exported data. I.e. It won't be split into multiple columns. */
    rowKey?: boolean;
    /** Indicates that exported data should be sorted by this column. */
    sortKey?: boolean;
}

/**
 * Exports the currently visible data from an ECharts chart instance into a 2D array.
 * If split groups are used in the chart, the exported data will include only the currently visible split group values.
 *
 * @param {Object} echartsOpt - The ECharts chart instance options.
 * @param {Array<{ name: string; key: string }>} [customColumns] - An optional array of columns to be used instead of the default series names. Each column should have a name (for the header) and a key (to identify the metric in the data source).
 * @returns {Array<Array>} A 2D array of the exported data.
 */
export function echartsExportDataSplitGroup(
    echartsOpt: any,
    customColumns?: ICustomColumn[]
): any[][] {
    console.log('Exporting visible data from chart instance:', echartsOpt);

    /**
     * Retrieves the selected series from the chart legend options.
     * @param opt
     * @returns An object where the keys are series names and the values are booleans indicating whether the series is selected (visible) or not.
     */
    const getVisibleSeriesFromLegend = (opt: any): any => {
        const selectedSeries = (() => {
            if (opt.legend && Array.isArray(opt.legend) && opt.legend[0]) {
                return opt.legend[0].selected || {};
            }
            return {};
        })();
        // Filter out the series that are not currently visible
        const visibleSeries = echartsOpt.series.filter((series: any) => {
            return selectedSeries[series.name] ?? true;
        });

        return visibleSeries;
    };

    /**
     * Gets the dataset associated with a series.
     * If the series has a datasetId, it returns the dataset with that id from the options.
     * Otherwise, it returns the first dataset in the options.
     * @param opt
     * @param series
     * @returns The dataset associated with the series.
     */
    const getSeriesDataset = (opt: any, series: any): any => {
        return series.datasetId
            ? opt.dataset.find(
                  (dataset: any) => dataset!.id === series.datasetId
              )
            : // fallback to first dataset
              opt.dataset[0];
    };

    /**
     * Transposes a 2D array (matrix) by converting rows to columns and columns to rows.
     * @param columns - A 2D array where each sub-array represents a column
     * @returns A new 2D array where the original columns become rows
     * @example
     * const data = [[1, 2, 3], [4, 5, 6]];
     * const result = pivot(data);
     * // result: [[1, 4], [2, 5], [3, 6]]
     */
    const pivot = (arr: any[][]): any[][] => {
        const firstRow = arr[0];
        return firstRow.map((_: any, columnIndex: number) =>
            arr.map((row) => row[columnIndex])
        );
    };

    // if more than one legend throw error
    if (
        echartsOpt.legend &&
        Array.isArray(echartsOpt.legend) &&
        echartsOpt.legend.length > 1
    ) {
        throw new Error('More than one legend is not supported.');
    }

    // if no data there is nothing to export
    if (!echartsOpt.series) {
        return [];
    }

    const dataExportHeader: string[] = [];
    const dataExport: any[][] = [dataExportHeader];

    if (
        !echartsOpt.dataset ||
        !echartsOpt.dataset[0] ||
        !echartsOpt.dataset[0].source
    ) {
        throw new Error('Dataset with source is required for data export.');
    }

    const chartSeries = echartsOpt.series;
    if (chartSeries === undefined || chartSeries.length === 0) {
        throw new Error('No series found in chart options.');
    }
    // Assume first dataset is the main dataset for export, this is a common pattern in BiViz ECharts options
    const sourceData = echartsOpt.dataset[0].source;
    const unpivotedColumns: any[][] = [];
    const isSplitColumnPresent = 'split_group' in sourceData[0];

    // get distinct and ordered values from the x axis
    // every visible series has the same x axis definition, so we can use the first one
    const getXAxisValues = (xAxisKey: string) => {
        const xValues = Array.from(
            new Set(sourceData.map((row: any) => row[xAxisKey]))
        );
        // check if all elements of xValues are numbers then sort as numbers. Else use default sort
        const isNumber = xValues.every((val: any) => typeof val === 'number');
        if (isNumber) {
            xValues.sort((a: any, b: any) => a - b);
        } else {
            xValues.sort();
        }
        return xValues;
    };

    if (!customColumns) {
        const xAxisKey = chartSeries[0].encode.x;
        let xAxisValues = getXAxisValues(xAxisKey);
        dataExportHeader.push(xAxisKey);
        chartSeries.forEach((s: any) => {
            dataExportHeader.push(s.name);
        });

        unpivotedColumns.push(xAxisValues);

        for (const series of chartSeries) {
            const metricKey = series.encode.y;

            const seriesDataset = getSeriesDataset(echartsOpt, series);

            if (!seriesDataset) {
                throw new Error(`seriesDataset is undefined`);
            }

            // if dataset contains source then we get values directly from there by matching x axis value.
            // This is the case when dataset is defined on series level and contains only relevant data for that series.
            // In this case we can just find matching x value in series dataset and take corresponding y value
            // without caring about split groups because they should be already applied in series dataset.
            if (seriesDataset.source) {
                unpivotedColumns.push(
                    xAxisValues.map((xValue: any) => {
                        const matchingRow = seriesDataset.source.find(
                            (row: any) => row[series.encode.x] === xValue
                        );
                        return matchingRow ? matchingRow[metricKey] : null;
                    })
                );
            }
            // datasource can contain transform of type filter with config.value which defines split group value.
            // In this case we need to find rows in main dataset with matching x value and split group value
            else if (seriesDataset.transform) {
                if (!(seriesDataset.transform.type == 'filter')) {
                    throw new Error(
                        'Series dataset have transform but no filter'
                    );
                }
                if (!isSplitColumnPresent) {
                    throw new Error(
                        'Dataset source must contain split_group column for split group based export.'
                    );
                }

                const splitGroupValue = seriesDataset.transform.config.value;

                unpivotedColumns.push(
                    xAxisValues.map((xValue: any) => {
                        const matchingRow = sourceData.find(
                            (row: any) =>
                                row[xAxisKey] === xValue &&
                                row['split_group'] === splitGroupValue
                        );

                        return matchingRow ? matchingRow[metricKey] : null;
                    })
                );
            }
        }
        dataExport.push(...pivot(unpivotedColumns));
        return dataExport;
    } else {
        // customColumns !== undefined
        // at least one column must have rowKey set to true to be used as x axis values
        if (!customColumns.some((col) => col.rowKey)) {
            throw new Error(
                'At least one column must have rowKey set to true.'
            );
        }

        const rowKeys = customColumns.filter((col) => col.rowKey);
        const splitGroupKey = 'split_group';

        const rowKeySourceData = sourceData.map((row: any) => {
            const rowKeyObj: any = {};
            rowKeys.forEach((rowKey) => {
                rowKeyObj[rowKey.key] = row[rowKey.key];
            });
            return rowKeyObj;
        });
        // Remove duplicates by creating a unique key from all rowKey values
        const rowKeyValues = Array.from(
            new Map(
                rowKeySourceData.map((row: any) => [JSON.stringify(row), row])
            ).values()
        );

        // Sort by sortKey if specified
        const sortKeyColumn = customColumns.find((col) => col.sortKey);
        if (sortKeyColumn) {
            rowKeyValues.sort((a: any, b: any) => {
                const aVal = a[sortKeyColumn.key];
                const bVal = b[sortKeyColumn.key];

                if (typeof aVal === 'number' && typeof bVal === 'number') {
                    return aVal - bVal;
                }

                return String(aVal).localeCompare(String(bVal));
            });
        } else {
            // sort by row keys to ensure consistent order in exported data
            rowKeyValues.sort((a: any, b: any) => {
                for (const rk of rowKeys) {
                    const aVal = a[rk.key];
                    const bVal = b[rk.key];
                    if (aVal === bVal) {
                        continue;
                    }
                    if (typeof aVal === 'number' && typeof bVal === 'number') {
                        return aVal - bVal;
                    }
                    return String(aVal).localeCompare(String(bVal));
                }
                return 0;
            });
        }

        if (isSplitColumnPresent) {
            const splitValues = Array.from(
                new Set(sourceData.map((row: any) => row[splitGroupKey]))
            );

            const pivotableColumns = customColumns.filter(
                (col) => col.rowKey !== true
            );

            rowKeys.forEach((col) => {
                dataExportHeader.push(col.key);
                unpivotedColumns.push(
                    rowKeyValues.map((row: any) => row[col.key])
                );
            });

            pivotableColumns.forEach((col) => {
                splitValues.forEach((splitVal) => {
                    dataExportHeader.push(`${splitVal}, ${col.name}`);
                    unpivotedColumns.push(
                        rowKeyValues.map((rowKey: any) => {
                            const matchingRow = sourceData.find(
                                (row: any) =>
                                    rowKeys.every(
                                        (rk) => row[rk.key] === rowKey[rk.key]
                                    ) && row['split_group'] === splitVal
                            );

                            return matchingRow ? matchingRow[col.key] : null;
                        })
                    );
                });
            });
        } else {
            dataExportHeader.push(...customColumns.map((col) => col.name));
            dataExport.push(
                ...sourceData.map((row: any) =>
                    customColumns.map((col) => row[col.key])
                )
            );
            return dataExport;
        }
        dataExport.push(...pivot(unpivotedColumns));
        return dataExport;
    }
}

/**
 * The options for exporting data.
 * @typedef {Object} DataExportOptions
 * @property {string} filename - The name of the file to be exported.
 */
interface DataExportOptions {
    filename: string;
}

/**
 * Creates and triggers the download of a CSV file from a 2D array of data.
 * @param {Array<Array>} dataForExport - The 2D array of data to be exported.
 * @param {DataExportOptions} options - The options for exporting data.
 */
export function downloadCSV(
    dataForExport: any[][],
    options: DataExportOptions
): void {
    let csvContent = 'data:text/csv;charset=utf-8,';
    dataForExport.forEach((row, index) => {
        if (index === 0) {
            csvContent +=
                row.map((headerCol) => `"${headerCol}"`).join(',') + '\r\n';
        } else {
            csvContent += row.join(',') + '\r\n';
        }
    });
    const encodedUri = encodeURI(csvContent);
    triggerDownload(encodedUri, `${options.filename}.csv`);
}

/**
 * Creates and triggers the download of an XLSX file from a 2D array of data.
 * @param {Array<Array>} dataForExport - The 2D array of data to be exported.
 * @param {DataExportOptions} options - The options for exporting data.
 */
export function downloadXLSX(
    dataForExport: any[][],
    options: DataExportOptions
): void {
    const ws = XLSX.utils.aoa_to_sheet(dataForExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, `${options.filename}.xlsx`);
}

export function copyToClipboard(
    dataForExport: any[][],
    options?: DataExportOptions
): void {
    navigator.clipboard.writeText(
        dataForExport
            .map((row, index) => {
                if (index === 0) {
                    return row.map((headerCol) => `"${headerCol}"`).join('\t');
                } else {
                    return row.join('\t');
                }
            })
            .join('\n')
    );
}

/**
 * Triggers the download of a file using a data URI.
 * @param {string} uri - The data URI of the file.
 * @param {string} filename - The name of the file to be downloaded.
 */
function triggerDownload(uri: string, filename: string): void {
    const link = document.createElement('a');
    link.setAttribute('href', uri);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

/**
 * Creates a data exporter function based on specified options.
 * @param {Object} exportOpt - The export options.
 * @param {'csv' | 'xlsx'} exportOpt.type - The file format for export.
 * @param {string} exportOpt.filename - The name of the file to be exported.
 * @param {function} [exportOpt.dataExporter] - The data exporter function to use.
 * @returns {function} A function that exports data from an ECharts instance.
 */
export function getDefaultDataExporter(exportOpt: {
    /** Export file type */
    type: 'csv' | 'xlsx' | 'clipboard';
    /** Export filename */
    filename: string;
    /** Data exporter function */
    dataExporter: (
        options: any,
        customColumns?: { name: string; key: string; splitable?: boolean }[]
    ) => any[][];
    /** An optional array of columns to be used instead of the default series names. Each column should have a name (for the header) and a key (to identify the metric in the data source). */
    customColumns?: { name: string; key: string; splitable?: boolean }[];
}): (echartsOpt: any) => void {
    // const downloadClb = exportOpt.type === 'csv' ? downloadCSV : downloadXLSX;
    const downloadClb = (() => {
        switch (exportOpt.type) {
            case 'csv':
                return downloadCSV;
            case 'xlsx':
                return downloadXLSX;
            case 'clipboard':
                return copyToClipboard;
        }
    })();
    // TODO create type with defaults
    return (options: any) => {
        const dataToExport = exportOpt.dataExporter(
            options,
            exportOpt.customColumns
        );
        downloadClb(dataToExport, { filename: exportOpt.filename });
    };
}
